import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger, DropdownMenuLabel,
} from './ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Bookmark, Plus, Trash2, ChevronDown, Zap, Eye, Filter,
  ArrowDownAZ, Clock, DollarSign, AlertTriangle, CheckCircle2,
  Users, Calendar, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  zap: Zap, eye: Eye, filter: Filter, clock: Clock, dollar: DollarSign,
  alert: AlertTriangle, check: CheckCircle2, users: Users, calendar: Calendar,
  file: FileText, sort: ArrowDownAZ, bookmark: Bookmark,
};

/**
 * SavedViews - Reusable saved filter/sort preset component
 * @param {string} page - Page identifier (invoices, tasks, time, docs)
 * @param {Array} presets - Built-in preset views [{name, icon, filters, sort}]
 * @param {Function} onApply - Called with {filters, sort} when a view is selected
 * @param {Object} currentFilters - Current filter state to save
 * @param {Object} currentSort - Current sort state to save
 */
export function SavedViews({ page, presets = [], onApply, currentFilters = {}, currentSort = {} }) {
  const { currentWorkspace } = useWorkspace();
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [activeView, setActiveView] = useState(null);

  const wsId = currentWorkspace?.workspace_id;

  const fetchViews = useCallback(async () => {
    if (!wsId) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/saved-views?page=${page}`);
      if (res.ok) {
        const data = await res.json();
        setViews(Array.isArray(data) ? data : []);
      }
    } catch (e) { console.error(e); }
  }, [wsId, page]);

  useEffect(() => { fetchViews(); }, [fetchViews]);

  const handleApply = (view) => {
    setActiveView(view.name);
    onApply({ filters: view.filters || {}, sort: view.sort || {} });
  };

  const handleSave = async () => {
    if (!viewName.trim()) return;
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/saved-views`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page,
          name: viewName.trim(),
          icon: 'bookmark',
          filters: currentFilters,
          sort: currentSort,
        }),
      });
      if (res.ok) {
        toast.success('View saved');
        setSaveOpen(false);
        setViewName('');
        fetchViews();
      }
    } catch (e) { toast.error('Failed to save view'); }
  };

  const handleDelete = async (viewId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/saved-views/${viewId}`, { method: 'DELETE' });
    fetchViews();
    toast.success('View removed');
  };

  const handleClear = () => {
    setActiveView(null);
    onApply({ filters: {}, sort: {} });
  };

  const allViews = [...presets.map((p, i) => ({ ...p, _isPreset: true, _key: `preset_${i}` })), ...views.map(v => ({ ...v, _isPreset: false, _key: v.view_id }))];

  return (
    <div className="flex items-center gap-2" data-testid="saved-views">
      {/* Quick-access preset pills */}
      <div className="flex items-center gap-1 overflow-x-auto">
        {presets.slice(0, 4).map((preset, i) => {
          const IconComp = ICON_MAP[preset.icon] || Zap;
          const isActive = activeView === preset.name;
          return (
            <button
              key={i}
              onClick={() => isActive ? handleClear() : handleApply(preset)}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap transition-all",
                isActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
              )}
              data-testid={`preset-${preset.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <IconComp className="w-3 h-3" />
              {preset.name}
            </button>
          );
        })}
      </div>

      {/* More views dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground" data-testid="saved-views-menu">
            <Bookmark className="w-3.5 h-3.5" />
            Views
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Quick Views</DropdownMenuLabel>
          {presets.map((preset, i) => {
            const IconComp = ICON_MAP[preset.icon] || Zap;
            return (
              <DropdownMenuItem key={`p_${i}`} onClick={() => handleApply(preset)} className="gap-2">
                <IconComp className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="flex-1">{preset.name}</span>
                {activeView === preset.name && <CheckCircle2 className="w-3 h-3 text-primary" />}
              </DropdownMenuItem>
            );
          })}

          {views.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Your Saved Views</DropdownMenuLabel>
              {views.map(view => (
                <DropdownMenuItem key={view.view_id} className="gap-2 group" onClick={() => handleApply(view)}>
                  <Bookmark className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="flex-1">{view.name}</span>
                  {activeView === view.name && <CheckCircle2 className="w-3 h-3 text-primary" />}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(view.view_id); }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3 h-3 text-destructive" />
                  </button>
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          {activeView && (
            <DropdownMenuItem onClick={handleClear} className="gap-2 text-muted-foreground">
              <Filter className="w-3.5 h-3.5" /> Clear Active View
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setSaveOpen(true)} className="gap-2">
            <Plus className="w-3.5 h-3.5" /> Save Current View
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save Dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save Current View</DialogTitle>
            <DialogDescription>Save your current filters and sort as a reusable view</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="View name (e.g., Overdue High-Value)"
              value={viewName}
              onChange={e => setViewName(e.target.value)}
              data-testid="save-view-name"
              onKeyDown={e => e.key === 'Enter' && handleSave()}
            />
            <div className="text-xs text-muted-foreground p-2 rounded bg-muted/50">
              <p className="font-medium mb-1">Current state being saved:</p>
              <p>Filters: {Object.entries(currentFilters).filter(([,v]) => v && v !== 'all').map(([k,v]) => `${k}=${v}`).join(', ') || 'None'}</p>
              <p>Sort: {currentSort.field ? `${currentSort.field} ${currentSort.dir}` : 'Default'}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!viewName.trim()} data-testid="confirm-save-view">
              <Bookmark className="w-4 h-4 mr-1" /> Save View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
